import { forwardRef, useImperativeHandle } from 'react';
import './style.scss';
import uniq from 'lodash/uniq';
import { TranslationService } from '../../../../services/translation.service';
import ValidationInfo from '../../../../validation/validation-info';
import type IValidatable from '../../../Shared/validatable.interface';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import RequiredAsterisk from '../../../Shared/RequiredAsterisk';
import ValidationEngine from '../../../../validation/validation-engine';
import { oresundstagStations } from './departureArrivalStations.constants';

export interface IDepartureArrivalSelectorState {
  value: {
    departureStation: string | undefined;
    arrivalStation: string | undefined;
  };
  validationInfo: ValidationInfo;
}

export interface IDepartureArrivalSelectorProps {
  id?: string;
  placeholderText?: string;
  analyticsCategory?: string | undefined;
  state: IDepartureArrivalSelectorState;
  onChange: (value: { departureStation: string; arrivalStation: string }) => void | undefined;
}

export default forwardRef<IValidatable, IDepartureArrivalSelectorProps>(function DepartureArrivalSelector({ id, placeholderText, state, onChange }: IDepartureArrivalSelectorProps, ref) {
  // === HELPER FUNCTIONS ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const departureValidationInfo = new ValidationInfo(`${TranslationService.get('common.from')} - `);
      const arrivalValidationInfo = new ValidationInfo(`${TranslationService.get('common.to')} - `);
      ValidationEngine.for(departureValidationInfo).required(state.value.departureStation, 'delaycompensationoresundstagformpage.fromto.anyrequiredoresundstag').validate();
      ValidationEngine.for(arrivalValidationInfo)
        .required(state.value.arrivalStation, 'delaycompensationoresundstagformpage.fromto.anyrequiredoresundstag')
        .if(!!state.value.arrivalStation)
        .notEqual(state.value.departureStation!, state.value.arrivalStation!, 'common.validation.fromto.samevalue')
        .endif()
        .validate();

      return new ValidationInfo('', [], {
        departureStation: departureValidationInfo,
        arrivalStation: arrivalValidationInfo,
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      const departureValidationInfo = new ValidationInfo(
        `${TranslationService.get('common.from')} - `,
        errors
          .filter((e) => e.name.toLocaleLowerCase() === 'departurestation')
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
        {}
      );
      const arrivalValidationInfo = new ValidationInfo(
        `${TranslationService.get('common.to')} - `,
        errors
          .filter((e) => e.name.toLocaleLowerCase() === 'arrivalstation')
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
        {}
      );

      return new ValidationInfo('', [], {
        departureStation: departureValidationInfo,
        arrivalStation: arrivalValidationInfo,
      });
    },
  }));

  return (
    <div id={id} className="from-to-selector position-relative">
      <div className={`rounded border border-gray-700 flex-wrap from-to-selector__select d-flex ${state.validationInfo.isInvalid ? 'border-red' : ''}`}>
        <div
          className={`d-flex flex-nowrap flex-grow-1 align-items-center position-relative ${
            state.validationInfo.children.departureStation?.isInvalid ? 'from-to-selector__location-select--invalid' : ''
          }`}
        >
          <label className="position-absolute ps-2 my-0" htmlFor="departure-arrival-departurestation">
            {TranslationService.get('common.from')}
            <RequiredAsterisk />
          </label>{' '}
          <select
            className={`form-select oresundstag-station-select position-relative ms-5 ${state.validationInfo.children.departureStation?.isInvalid ? 'is-invalid' : ''}`}
            id="departure-arrival-departurestation"
            name="departure-arrival-departurestation"
            value={state.value.departureStation}
            onChange={(e) => onChange({ departureStation: e.target.value, arrivalStation: state.value.arrivalStation! })}
            defaultValue={0}
            aria-invalid={state.validationInfo.isInvalid ? 'true' : 'false'}
          >
            <option value={0} disabled>
              {placeholderText}
            </option>
            {oresundstagStations.from.map((station) => (
              <option className="ps-1" key={station} value={station}>
                {station}
              </option>
            ))}
          </select>
        </div>
        <div className="from-to-selector__change-direction ms-2 me-2 justify-content-center align-items-center border-bottom border-gray-700 position-relative"></div>
        <div
          className={`d-flex flex-nowrap flex-grow-1 align-items-center position-relative ${
            state.validationInfo.children.arrivalStation?.isInvalid ? 'from-to-selector__location-select--invalid' : ''
          }`}
        >
          <label className="position-absolute ps-2 my-0" htmlFor="departure-arrival-arrivalstation">
            {TranslationService.get('common.to')}
            <RequiredAsterisk />
          </label>{' '}
          <select
            className={`form-select oresundstag-station-select position-relative ms-5 ${state.validationInfo.children.arrivalStation?.isInvalid ? 'is-invalid' : ''}`}
            id="departure-arrival-arrivalstation"
            name="departure-arrival-arrivalstation"
            value={state.value.arrivalStation}
            onChange={(e) => onChange({ departureStation: state.value.departureStation!, arrivalStation: e.target.value })}
            defaultValue={0}
            aria-invalid={state.validationInfo.isInvalid ? 'true' : 'false'}
          >
            <option value={0} disabled>
              {placeholderText}
            </option>
            {oresundstagStations.to.map((station) => (
              <option key={station} value={station}>
                {station}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div role="alert">
        {state.validationInfo.isInvalid && (
          <div id="from-to-selector-error" className="invalid-feedback d-block">
            {uniq(state.validationInfo.children.departureStation.errors.concat(state.validationInfo.children.arrivalStation.errors)).map((error) => (
              <div key={error}>{error}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
